/**
 * Returns a promise which resolves with the event data when the 'modal-complete' event is
 * captured and the name matches the fired modal.
 *
 * @param { string } modal_name
 * @param { number } [timeout]
 * @param { (ev: { [string]: any } ) => string } [callback]
 * @return Promise
 */
export function onModal(modal_name, timeout, callback) {
  return new Promise((res) => {
    // wait for closed event from somewhere
    EventBus.$on(`modal-complete`, (ev) => {
      // if this is the modal in question:
      if (ev.name === modal_name) {
        // close it
        this.$modal.hide(modal_name);
        // resolve promise and invoke any callback if passed
        res(callback ? callback(ev) : ev);
      }
    });
    // or timeout
    if (timeout) {
      setTimeout(() => res(false), timeout);
    }
  });
}

/**
 * 'Fires' a modal, meaning it's opened. Then returns a promise equivalent to calling `onModal`.
 *
 * @param { string } modal_name
 * @param { number } [timeout]
 * @param { (ev: { [string]: any } ) => string } [callback]
 * @return Promise
 */
export function fireModal(modal_name, timeout, callback) {
  this.$modal.show(modal_name); // displays the vjs-modal
  return this.onModal(modal_name, timeout, callback);
}

export function hideModal(modal_name) {
  this.$modal.hide(modal_name);
}

/**
 * Fires a confirm modal, then invokes the supplied callback on the supplied models, returning this value.
 *
 * **The corresponding modal type must be included in the calling component's markup (must be imported & used).**
 *
 * @template T
 * @param { any[] } models Objects to pass to the supplied `callback` on modal confirm.
 * @param { (...any) => T } callback Some type of callback accepting `models` on modal confirm.
 * @param { string } modal_name The name of the modal to show.
 * @return { false | T }
 */
export async function confirmModalThen(models, callback, modal_name) {
  this.staged_models = models;
  const name = modal_name ?? 'confirm';
  // here we want to wait for the the modal result, also firing the modal on the next tick
  const res = await new Promise((res) => {
    this.$nextTick(async () => {
      res(await this.fireModal(name));
    });
  });

  if (!res.data.confirm) {
    return false;
  }

  return callback(...models);
}
